import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FaTimes } from "react-icons/fa";

import axiosOrganizationInstance from "../organization/AxiosOrganizationInstance";
import axiosCustomerInstance from "../api/AxiosCustomerInstance";
import { getAuthHeaders } from "../organization/organizationStorage";
import axiosRestaurantInstance from "../restaurant/AxiosRestaurantInstance";
import axiosGroceryInstance from "../restaurant/AxiosGroceryInstance";
import { GROCERY_API_PATHS } from "../constants/apiPaths";
import { extractRestaurantId, extractRestaurantName } from "../restaurant/restaurantApi";
import { extractOrganizationId } from "../organization/organizationApi";
import { extractStoreId, extractStoreName, listStores } from "../store/storeApi";
import EatsAdminLayout from "./EatsAdminLayout";
import PickupHubsPage from "./PickupHubsPage";
import JuniorEatsSubscriptionsPage from "./JuniorEatsSubscriptionsPage";
import JuniorEatsMainMenuPage from "./JuniorEatsMainMenuPage";
import GlobalMenuMasterPage from "./GlobalMenuMasterPage";
import { formatMobileDisplayOrFallback } from "../restaurant/formatDisplay";
import { useAutoDismissMessage } from "../utils/useAutoDismissMessage";
import "./EatsAdminCreatePage.css";
import "./EatsAdminDashboard.css";

const VALID_TABS = ["home", "restaurants", "organizations", "stores", "customers", "subscriptions", "jreats-menu", "menu-master", "pickup-hubs"];
const TAB_TITLE = {
  home: "Dashboard",
  restaurants: "Restaurants",
  organizations: "Organizations",
  stores: "Stores",
  customers: "Customers",
  subscriptions: "Subscriptions",
  "jreats-menu": "Jr Eats Menu",
  "menu-master": "Menu Master",
  "pickup-hubs": "Pickup Hubs",
};

const getOrganizationDisplayName = (org) =>
  org?.name || org?.organizationName || org?.Name || "-";

const getOrganizationDisplayCity = (org) => {
  if (org?.city) {
    return org.city;
  }
  const addresses = org?.addresses;
  if (Array.isArray(addresses) && addresses.length > 0) {
    return addresses[0]?.city || "-";
  }
  return org?.postalCode || "-";
};

const getRestaurantDisplayCity = (restaurant) => {
  if (restaurant?.city) {
    return restaurant.city;
  }
  const addresses = restaurant?.addresses;
  if (Array.isArray(addresses) && addresses.length > 0) {
    return addresses[0]?.city || "-";
  }
  return "-";
};

const getStoreDisplayCity = (store) => store?.city || "-";

const formatDashboardCount = (value, loading) => {
  if (loading) return "…";
  if (value == null) return "—";
  return value;
};

const renderStatusBadge = (status) => {
  if (!status) return "-";
  const normalized = String(status).toUpperCase();
  const className =
    normalized === "ACTIVE"
      ? "eats-admin-status eats-admin-status--active"
      : normalized === "INACTIVE"
        ? "eats-admin-status eats-admin-status--inactive"
        : "eats-admin-status eats-admin-status--default";
  return <span className={className}>{status}</span>;
};

const ADMIN_ROWS_PER_PAGE = 10;

const AdminTablePagination = ({ page, totalItems, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / ADMIN_ROWS_PER_PAGE));
  if (totalItems <= ADMIN_ROWS_PER_PAGE) return null;
  const start = (page - 1) * ADMIN_ROWS_PER_PAGE + 1;
  const end = Math.min(page * ADMIN_ROWS_PER_PAGE, totalItems);
  return (
    <div className="eats-admin-pagination">
      <span className="eats-admin-pagination__info">
        {start}-{end} of {totalItems}
      </span>
      <div className="eats-admin-pagination__controls">
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--ghost"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          Prev
        </button>
        <span className="eats-admin-pagination__page">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          className="eats-admin-btn eats-admin-btn--ghost"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const AdminPendingToggle = ({ count, active, onOpen, onClose }) => {
  if (!count) return null;
  return (
    <button
      type="button"
      className={`eats-admin-pending-toggle is-alert${active ? " is-active" : ""}`}
      onClick={active ? onClose : onOpen}
    >
      Pending ({count})
    </button>
  );
};

const AdminClearableInput = ({ value, onChange, onClear, placeholder }) => (
  <div className="eats-admin-input-wrap">
    <input
      className="eats-admin-input"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
    {value ? (
      <button
        type="button"
        className="eats-admin-input-clear"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClear?.();
        }}
        aria-label="Clear search"
      >
        <FaTimes aria-hidden />
      </button>
    ) : null}
  </div>
);

const EatsAdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("home");
  const navigate = useNavigate();

  const [orgSearch, setOrgSearch] = useState({ name: "", postal: "" });
  const [orgResults, setOrgResults] = useState([]);
  const [orgError, setOrgError] = useState("");
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgPage, setOrgPage] = useState(1);

  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantStatusFilter, setRestaurantStatusFilter] = useState(
    () => (searchParams.get("tab") === "restaurants" && searchParams.get("view") === "pending" ? "PENDING" : "ACTIVE")
  );
  const [restaurantResults, setRestaurantResults] = useState([]);
  const [restaurantError, setRestaurantError] = useState("");
  const [restaurantLoading, setRestaurantLoading] = useState(false);
  const [restaurantPage, setRestaurantPage] = useState(1);
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [pendingOrganizations, setPendingOrganizations] = useState([]);
  const [pendingStores, setPendingStores] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    organizations: null,
    restaurants: null,
    stores: null,
    customers: null,
  });
  const [dashboardStatsLoading, setDashboardStatsLoading] = useState(true);

  const [storeSearch, setStoreSearch] = useState("");
  const [storeResults, setStoreResults] = useState([]);
  const [storeError, setStoreError] = useState("");
  const [storeLoading, setStoreLoading] = useState(false);
  const [storePage, setStorePage] = useState(1);

  const [customerCriteria, setCustomerCriteria] = useState({
    name: "",
    contactNo: "",
    createdSource: "",
  });
  const [customerResults, setCustomerResults] = useState([]);
  const [customerError, setCustomerError] = useState("");
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  useAutoDismissMessage(orgError, setOrgError);
  useAutoDismissMessage(restaurantError, setRestaurantError);
  useAutoDismissMessage(storeError, setStoreError);
  useAutoDismissMessage(customerError, setCustomerError);
  const organizationView = searchParams.get("view") || "default";
  const storeView = searchParams.get("view") || "default";

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "pickup-pricing") {
      setActiveTab("pickup-hubs");
      setSearchParams({ tab: "pickup-hubs" }, { replace: true });
      return;
    }
    if (tab && VALID_TABS.includes(tab)) {
      setActiveTab(tab);
      return;
    }
    setActiveTab("home");
    setSearchParams({ tab: "home" }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadPendingCompanies = async () => {
      try {
        const res = await axiosRestaurantInstance.get("/restaurant/pending-companies", {
          headers: getAuthHeaders(),
        });
        const list = Array.isArray(res?.data?.companies) ? res.data.companies.filter((c) => c && c.restaurantId) : [];
        setPendingCompanies(list);
      } catch (error) {
        console.error("Failed loading pending companies from backend:", error?.response?.data || error?.message);
        setPendingCompanies([]);
      }
    };
    const loadPendingOrganizations = async () => {
      try {
        const res = await axiosOrganizationInstance.get("/organization/pending-organizations", {
          headers: getAuthHeaders(),
        });
        const list = Array.isArray(res?.data?.organizations)
          ? res.data.organizations.filter((o) => o && o.organizationId)
          : [];
        setPendingOrganizations(list);
      } catch (error) {
        console.error(
          "Failed loading pending organizations from backend:",
          error?.response?.data || error?.message
        );
        setPendingOrganizations([]);
      }
    };

    const loadPendingStores = async () => {
      try {
        const res = await axiosGroceryInstance.get(GROCERY_API_PATHS.pendingStores, {
          headers: getAuthHeaders(),
        });
        const list = Array.isArray(res?.data?.stores)
          ? res.data.stores.filter((s) => s && s.storeId)
          : [];
        setPendingStores(list);
      } catch (error) {
        console.error("Failed loading pending stores from backend:", error?.response?.data || error?.message);
        setPendingStores([]);
      }
    };

    const loadDashboardStats = async () => {
      setDashboardStatsLoading(true);
      try {
        const res = await axiosOrganizationInstance.get("/admin/dashboard-stats", {
          headers: getAuthHeaders(),
        });
        const data = res?.data || {};
        setDashboardStats({
          organizations: Number(data.organizations) || 0,
          restaurants: Number(data.restaurants) || 0,
          stores: Number(data.stores) || 0,
          customers: Number(data.customers) || 0,
        });
      } catch (error) {
        console.error("Failed loading dashboard stats:", error?.response?.data || error?.message);
        setDashboardStats({
          organizations: null,
          restaurants: null,
          stores: null,
          customers: null,
        });
      } finally {
        setDashboardStatsLoading(false);
      }
    };

    loadPendingCompanies();
    loadPendingOrganizations();
    loadPendingStores();
    loadDashboardStats();
    const onFocus = () => {
      loadPendingCompanies();
      loadPendingOrganizations();
      loadPendingStores();
      loadDashboardStats();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    if (restaurantStatusFilter !== "PENDING") return;
    if (restaurantSearch.trim()) return;
    setRestaurantResults(pendingCompanies);
  }, [pendingCompanies, restaurantStatusFilter, restaurantSearch]);

  const continuePendingRestaurantCreate = (restaurantId) => {
    navigate(`/eats/admin/restaurants/create?restaurantId=${restaurantId}`);
  };

  const continuePendingOrganizationCreate = (organizationId) => {
    navigate(`/eats/admin/organizations/create?organizationId=${organizationId}`);
  };

  const continuePendingStoreCreate = (storeId) => {
    navigate(`/eats/admin/stores/create?storeId=${storeId}`);
  };

  const openPendingOrganizationsView = () => {
    setSearchParams({ tab: "organizations", view: "pending" }, { replace: true });
  };

  const openOrganizationsDefaultView = () => {
    setSearchParams({ tab: "organizations" }, { replace: true });
  };

  const handleTabClick = (tab) => {
    if (!VALID_TABS.includes(tab)) return;
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  const searchOrganizations = async () => {
    const name = orgSearch.name.trim();
    const postal = orgSearch.postal.trim();
    if (!name && !postal) {
      setOrgError("Enter an organization name or postal code to search.");
      setOrgResults([]);
      return;
    }

    setOrgLoading(true);
    setOrgError("");
    setOrgPage(1);
    try {
      const hasPostal = !!postal;
      const endpoint = hasPostal ? "/organization/search-postal" : "/organization/search";
      const params = hasPostal
        ? { postalCode: postal, name: name || undefined }
        : { name: name || undefined };
      const res = await axiosOrganizationInstance.get(endpoint, {
        params,
        headers: getAuthHeaders(),
      });
      const rows = Array.isArray(res.data) ? res.data : res.data?.organizations || [];
      if (!rows.length) {
        setOrgError("No organizations found.");
        setOrgResults([]);
      } else {
        setOrgResults(rows);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        setOrgError("Session expired. Please sign in again.");
      } else {
        setOrgError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to search organizations"
        );
      }
      setOrgResults([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const searchRestaurants = async () => {
    setRestaurantLoading(true);
    setRestaurantError("");
    setRestaurantPage(1);
    try {
      if (restaurantStatusFilter === "PENDING") {
        const query = restaurantSearch.trim().toLowerCase();
        const rows = pendingCompanies.filter((c) => {
          if (!query) return true;
          const name = String(c.restaurantName || "").toLowerCase();
          const code = String(c.restaurantShortCode || "").toLowerCase();
          const id = String(c.restaurantId || "").toLowerCase();
          return name.includes(query) || code.includes(query) || id.includes(query);
        });
        setRestaurantResults(rows);
        return;
      }

      const res = await axiosRestaurantInstance.get("/restaurant/search", {
        params: { searchText: restaurantSearch.trim() || undefined },
      });
      const rows = res?.data?.restaurants || res?.data || [];
      const list = Array.isArray(rows) ? rows : [];
      setRestaurantResults(
        restaurantStatusFilter === "ALL"
          ? list
          : list.filter((r) => String(r.status || "").toUpperCase() === restaurantStatusFilter)
      );
    } catch (e) {
      setRestaurantError(e?.response?.data?.message || e?.response?.data?.error || "Failed to search restaurants");
      setRestaurantResults([]);
    } finally {
      setRestaurantLoading(false);
    }
  };

  const searchStores = async () => {
    const query = storeSearch.trim();
    if (!query) {
      setStoreError("Enter a store name, code, or id to search.");
      setStoreResults([]);
      return;
    }

    setStoreLoading(true);
    setStoreError("");
    setStorePage(1);
    try {
      const rows = await listStores();
      const normalizedQuery = query.toLowerCase();
      const filtered = rows.filter((s) => {
        const name = String(extractStoreName(s) || "").toLowerCase();
        const code = String(s.shortCode || "").toLowerCase();
        const id = String(extractStoreId(s) || "");
        return name.includes(normalizedQuery) || code.includes(normalizedQuery) || id.includes(query);
      });
      if (filtered.length === 0) {
        setStoreError("No stores found.");
        setStoreResults([]);
      } else {
        setStoreResults(filtered);
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        setStoreError("Session expired. Please sign in again.");
      } else {
        setStoreError(e?.response?.data?.message || e?.response?.data?.error || "Failed to search stores");
      }
      setStoreResults([]);
    } finally {
      setStoreLoading(false);
    }
  };

  const clearCustomerFilterField = (field) => {
    setCustomerCriteria((prev) => {
      const next = { ...prev, [field]: "" };
      const allEmpty = Object.values(next).every((v) => String(v || "").trim() === "");
      if (allEmpty) {
        setCustomerResults([]);
        setCustomerError("");
      }
      return next;
    });
  };

  const clearOrgFilterField = (field) => {
    setOrgSearch((prev) => {
      const next = { ...prev, [field]: "" };
      const allEmpty = Object.values(next).every((v) => String(v || "").trim() === "");
      if (allEmpty) {
        setOrgResults([]);
        setOrgError("");
      }
      return next;
    });
  };

  const searchCustomers = async () => {
    const name = String(customerCriteria.name || "").trim();
    const contactNo = String(customerCriteria.contactNo || "").trim();
    const createdSource = String(customerCriteria.createdSource || "").trim();

    if (!name && !contactNo && !createdSource) {
      setCustomerError("Enter at least one search criterion.");
      setCustomerResults([]);
      return;
    }

    const params = {};
    if (name) params.name = name;
    if (contactNo) params.contactNo = contactNo;
    if (createdSource) params.createdSource = createdSource;

    setCustomerLoading(true);
    setCustomerError("");
    setCustomerPage(1);
    try {
      const res = await axiosCustomerInstance.get("/customers/search", {
        params,
        headers: getAuthHeaders(),
      });
      const rows = res?.data?.customers || [];
      setCustomerResults(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setCustomerError(e?.response?.data?.message || e?.response?.data?.error || "Failed to search customers");
      setCustomerResults([]);
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleRestaurantStatusFilterChange = (value) => {
    setRestaurantStatusFilter(value);
    setRestaurantPage(1);
    setRestaurantError("");
    if (value === "PENDING") {
      setRestaurantResults(pendingCompanies);
      setSearchParams({ tab: "restaurants", view: "pending" }, { replace: true });
    } else {
      setRestaurantResults([]);
      setSearchParams({ tab: "restaurants" }, { replace: true });
    }
  };

  const openPendingStoresView = () => {
    setSearchParams({ tab: "stores", view: "pending" }, { replace: true });
  };

  const openStoresDefaultView = () => {
    setSearchParams({ tab: "stores" }, { replace: true });
  };

  return (
    <EatsAdminLayout activeTab={activeTab}>
          <h2 className="eats-admin-dashboard__title">{TAB_TITLE[activeTab] || "Dashboard"}</h2>
          <p className="eats-admin-dashboard__subtitle">
            Search restaurants, organizations, stores, and customers. Manage creation flows from this admin area.
          </p>

          <div className="eats-admin-dashboard">
            {activeTab === "home" ? (
              <div className="eats-admin-panel">
                <h3 className="eats-admin-panel__heading">Home</h3>
                <div className="eats-admin-stat-grid">
                  <div className="eats-admin-stat-card">
                    <div className="eats-admin-stat-card__label">Restaurants</div>
                    <div className="eats-admin-stat-card__value">
                      {formatDashboardCount(dashboardStats.restaurants, dashboardStatsLoading)}
                    </div>
                    <div className="eats-admin-stat-card__meta">
                      {pendingCompanies.length} pending setup
                    </div>
                  </div>
                  <div className="eats-admin-stat-card">
                    <div className="eats-admin-stat-card__label">Organizations</div>
                    <div className="eats-admin-stat-card__value">
                      {formatDashboardCount(dashboardStats.organizations, dashboardStatsLoading)}
                    </div>
                    <div className="eats-admin-stat-card__meta">
                      {pendingOrganizations.length} pending setup
                    </div>
                  </div>
                  <div className="eats-admin-stat-card">
                    <div className="eats-admin-stat-card__label">Stores</div>
                    <div className="eats-admin-stat-card__value">
                      {formatDashboardCount(dashboardStats.stores, dashboardStatsLoading)}
                    </div>
                    <div className="eats-admin-stat-card__meta">
                      {pendingStores.length} pending setup
                    </div>
                  </div>
                  <div className="eats-admin-stat-card">
                    <div className="eats-admin-stat-card__label">Customers</div>
                    <div className="eats-admin-stat-card__value">
                      {formatDashboardCount(dashboardStats.customers, dashboardStatsLoading)}
                    </div>
                    <div className="eats-admin-stat-card__meta">Registered customers</div>
                  </div>
                </div>
                <ul className="eats-admin-hint-list">
                  <li>Use the left sidebar to switch modules.</li>
                  <li>Default dashboard route opens this Home view.</li>
                  <li>Create restaurant, organization, and store open in admin internal pages.</li>
                </ul>
              </div>
            ) : null}

            {activeTab === "organizations" ? (
              <div className="eats-admin-panel">
                <div className="eats-admin-search-bar eats-admin-search-bar--organizations">
                  <AdminClearableInput
                    placeholder="Search by name"
                    value={orgSearch.name}
                    onChange={(e) => setOrgSearch((p) => ({ ...p, name: e.target.value }))}
                    onClear={() => clearOrgFilterField("name")}
                  />
                  <AdminClearableInput
                    placeholder="Postal code (optional)"
                    value={orgSearch.postal}
                    onChange={(e) => setOrgSearch((p) => ({ ...p, postal: e.target.value }))}
                    onClear={() => clearOrgFilterField("postal")}
                  />
                  <button
                    type="button"
                    onClick={searchOrganizations}
                    disabled={orgLoading}
                    className="eats-admin-btn eats-admin-btn--primary"
                  >
                    {orgLoading ? "Searching..." : "Search"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/eats/admin/organizations/create")}
                    className="eats-admin-btn eats-admin-btn--create"
                  >
                    Create Organization
                  </button>
                  <AdminPendingToggle
                    count={pendingOrganizations.length}
                    active={organizationView === "pending"}
                    onOpen={openPendingOrganizationsView}
                    onClose={openOrganizationsDefaultView}
                  />
                </div>

                {organizationView === "pending" ? (
                  <div className="eats-admin-subpanel">
                    <div className="eats-admin-subpanel__header">Pending Organizations List</div>
                    <div className="eats-admin-subpanel__body">
                      <table className="eats-admin-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Code</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingOrganizations.length === 0 ? (
                            <tr><td colSpan={5} className="eats-admin-table__empty">No pending organizations.</td></tr>
                          ) : pendingOrganizations.map((o, i) => (
                            <tr key={`${o.organizationId || i}`}>
                              <td>{o.organizationId || "-"}</td>
                              <td>{o.organizationName || "-"}</td>
                              <td>{o.organizationCode || "-"}</td>
                              <td>{renderStatusBadge(o.status)}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => continuePendingOrganizationCreate(o.organizationId)}
                                  className="eats-admin-btn eats-admin-btn--ghost"
                                >
                                  Continue Create
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
                {orgError ? <div className="eats-admin-error">{orgError}</div> : null}
                <div className="eats-admin-table-wrap">
                  <table className="eats-admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Code</th>
                        <th>City</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgResults.length === 0 ? (
                        <tr><td colSpan={4} className="eats-admin-table__empty">No organization results.</td></tr>
                      ) : orgResults
                        .slice((orgPage - 1) * ADMIN_ROWS_PER_PAGE, orgPage * ADMIN_ROWS_PER_PAGE)
                        .map((o, i) => {
                        const id = extractOrganizationId(o);
                        const name = getOrganizationDisplayName(o);
                        return (
                        <tr key={`${id || i}`}>
                          <td>{id || "-"}</td>
                          <td>
                            {id ? (
                              <Link to={`/eats/admin/organizations/${id}`} className="eats-admin-link">
                                {name}
                              </Link>
                            ) : (
                              name
                            )}
                          </td>
                          <td>{o.organizationCode || "-"}</td>
                          <td>{getOrganizationDisplayCity(o)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <AdminTablePagination
                  page={orgPage}
                  totalItems={orgResults.length}
                  onPageChange={setOrgPage}
                />
              </div>
            ) : null}

            {activeTab === "restaurants" ? (
              <div className="eats-admin-panel">
                <div className="eats-admin-search-bar eats-admin-search-bar--restaurants">
                  <AdminClearableInput
                    placeholder="Search by restaurant text"
                    value={restaurantSearch}
                    onChange={(e) => setRestaurantSearch(e.target.value)}
                    onClear={() => {
                      setRestaurantSearch("");
                      setRestaurantError("");
                      setRestaurantResults(restaurantStatusFilter === "PENDING" ? pendingCompanies : []);
                    }}
                  />
                  <select
                    className="eats-admin-input eats-admin-status-filter"
                    value={restaurantStatusFilter}
                    onChange={(e) => handleRestaurantStatusFilterChange(e.target.value)}
                    aria-label="Restaurant status"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="PENDING">Pending</option>
                    <option value="ALL">All</option>
                  </select>
                  <button
                    type="button"
                    onClick={searchRestaurants}
                    disabled={restaurantLoading}
                    className="eats-admin-btn eats-admin-btn--primary"
                  >
                    {restaurantLoading ? "Searching..." : "Search"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/eats/admin/restaurants/create")}
                    className="eats-admin-btn eats-admin-btn--create"
                  >
                    Create Restaurant
                  </button>
                </div>

                {restaurantError ? <div className="eats-admin-error">{restaurantError}</div> : null}
                <div className="eats-admin-table-wrap">
                  <table className="eats-admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        {restaurantStatusFilter === "PENDING" ? <th>Short Code</th> : <th>City</th>}
                        <th>Status</th>
                        {restaurantStatusFilter === "PENDING" ? <th>Action</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {restaurantResults.length === 0 ? (
                        <tr>
                          <td colSpan={restaurantStatusFilter === "PENDING" ? 5 : 4} className="eats-admin-table__empty">
                            {restaurantStatusFilter === "PENDING"
                              ? "No pending restaurants."
                              : "No restaurant results."}
                          </td>
                        </tr>
                      ) : restaurantResults
                        .slice((restaurantPage - 1) * ADMIN_ROWS_PER_PAGE, restaurantPage * ADMIN_ROWS_PER_PAGE)
                        .map((r, i) => {
                        const id = extractRestaurantId(r) || r.restaurantId;
                        const name = extractRestaurantName(r) || r.restaurantName || "-";
                        return (
                        <tr key={`${id || i}`}>
                          <td>{id || "-"}</td>
                          <td>
                            {id ? (
                              <Link to={`/eats/admin/restaurants/${id}`} className="eats-admin-link">
                                {name}
                              </Link>
                            ) : (
                              name
                            )}
                          </td>
                          {restaurantStatusFilter === "PENDING" ? (
                            <td>{r.restaurantShortCode || "-"}</td>
                          ) : (
                            <td>{getRestaurantDisplayCity(r)}</td>
                          )}
                          <td>{renderStatusBadge(r.status)}</td>
                          {restaurantStatusFilter === "PENDING" ? (
                            <td>
                              <button
                                type="button"
                                onClick={() => continuePendingRestaurantCreate(id)}
                                className="eats-admin-btn eats-admin-btn--ghost"
                              >
                                Continue Create
                              </button>
                            </td>
                          ) : null}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <AdminTablePagination
                  page={restaurantPage}
                  totalItems={restaurantResults.length}
                  onPageChange={setRestaurantPage}
                />
              </div>
            ) : null}

            {activeTab === "stores" ? (
              <div className="eats-admin-panel">
                <div className="eats-admin-search-bar eats-admin-search-bar--stores">
                  <AdminClearableInput
                    placeholder="Search by store name, code, or ID"
                    value={storeSearch}
                    onChange={(e) => setStoreSearch(e.target.value)}
                    onClear={() => {
                      setStoreSearch("");
                      setStoreResults([]);
                      setStoreError("");
                    }}
                  />
                  <button
                    type="button"
                    onClick={searchStores}
                    disabled={storeLoading}
                    className="eats-admin-btn eats-admin-btn--primary"
                  >
                    {storeLoading ? "Searching..." : "Search"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/eats/admin/stores/create")}
                    className="eats-admin-btn eats-admin-btn--create"
                  >
                    Create Store
                  </button>
                  <AdminPendingToggle
                    count={pendingStores.length}
                    active={storeView === "pending"}
                    onOpen={openPendingStoresView}
                    onClose={openStoresDefaultView}
                  />
                </div>

                {storeView === "pending" ? (
                  <div className="eats-admin-subpanel">
                    <div className="eats-admin-subpanel__header">Pending Stores List</div>
                    <div className="eats-admin-subpanel__body">
                      <table className="eats-admin-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Short Code</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingStores.length === 0 ? (
                            <tr><td colSpan={5} className="eats-admin-table__empty">No pending stores.</td></tr>
                          ) : pendingStores.map((s, i) => (
                            <tr key={`${s.storeId || i}`}>
                              <td>{s.storeId || "-"}</td>
                              <td>{s.storeName || "-"}</td>
                              <td>{s.shortCode || "-"}</td>
                              <td>{renderStatusBadge(s.status)}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => continuePendingStoreCreate(s.storeId)}
                                  className="eats-admin-btn eats-admin-btn--ghost"
                                >
                                  Continue Create
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
                {storeError ? <div className="eats-admin-error">{storeError}</div> : null}
                <div className="eats-admin-table-wrap">
                  <table className="eats-admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>City</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storeResults.length === 0 ? (
                        <tr><td colSpan={4} className="eats-admin-table__empty">No store results.</td></tr>
                      ) : storeResults
                        .slice((storePage - 1) * ADMIN_ROWS_PER_PAGE, storePage * ADMIN_ROWS_PER_PAGE)
                        .map((s, i) => {
                        const id = extractStoreId(s);
                        const name = extractStoreName(s) || "-";
                        return (
                        <tr key={`${id || i}`}>
                          <td>{id || "-"}</td>
                          <td>
                            {id ? (
                              <Link to={`/eats/admin/stores/${id}`} className="eats-admin-link">
                                {name}
                              </Link>
                            ) : (
                              name
                            )}
                          </td>
                          <td>{getStoreDisplayCity(s)}</td>
                          <td>{renderStatusBadge(s.status)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <AdminTablePagination
                  page={storePage}
                  totalItems={storeResults.length}
                  onPageChange={setStorePage}
                />
              </div>
            ) : null}

            {activeTab === "customers" ? (
              <div className="eats-admin-panel">
                <div className="eats-admin-search-bar eats-admin-search-bar--customers">
                  {[
                    ["name", "Name"],
                    ["contactNo", "Contact Number"],
                    ["createdSource", "Created Source"],
                  ].map(([k, label]) => (
                    <AdminClearableInput
                      key={k}
                      placeholder={label}
                      value={customerCriteria[k]}
                      onChange={(e) => setCustomerCriteria((p) => ({ ...p, [k]: e.target.value }))}
                      onClear={() => clearCustomerFilterField(k)}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={searchCustomers}
                    disabled={customerLoading}
                    className="eats-admin-btn eats-admin-btn--primary"
                  >
                    {customerLoading ? "Searching..." : "Search"}
                  </button>
                </div>
                {customerError ? <div className="eats-admin-error">{customerError}</div> : null}
                <div className="eats-admin-table-wrap eats-admin-table-wrap--tall">
                  <table className="eats-admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Contact</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerResults.length === 0 ? (
                        <tr><td colSpan={5} className="eats-admin-table__empty">No customer results.</td></tr>
                      ) : customerResults
                        .slice((customerPage - 1) * ADMIN_ROWS_PER_PAGE, customerPage * ADMIN_ROWS_PER_PAGE)
                        .map((c) => (
                        <tr key={c.customerId}>
                          <td>{c.customerId}</td>
                          <td>{`${c.firstName || ""} ${c.lastName || ""}`.trim() || "-"}</td>
                          <td>{c.email || "-"}</td>
                          <td style={{ whiteSpace: "nowrap", minWidth: 130 }}>{formatMobileDisplayOrFallback(c.contactNumber, "-")}</td>
                          <td>{c.createdSource || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <AdminTablePagination
                  page={customerPage}
                  totalItems={customerResults.length}
                  onPageChange={setCustomerPage}
                />
              </div>
            ) : null}

            {activeTab === "subscriptions" ? (
              <div className="eats-admin-panel">
                <JuniorEatsSubscriptionsPage />
              </div>
            ) : null}

            {activeTab === "jreats-menu" ? (
              <div className="eats-admin-panel">
                <JuniorEatsMainMenuPage />
              </div>
            ) : null}

            {activeTab === "menu-master" ? (
              <div className="eats-admin-panel">
                <GlobalMenuMasterPage />
              </div>
            ) : null}

            {activeTab === "pickup-hubs" ? (
              <div className="eats-admin-panel">
                <PickupHubsPage />
              </div>
            ) : null}
          </div>
    </EatsAdminLayout>
  );
};

export default EatsAdminDashboard;
